package com.fantasytenis.web.navigation

import kotlinx.browser.window

/**
 * Navigation routes for the web application.
 */
sealed class Route(val path: String) {
    // Public routes (no auth required)
    data object Login : Route("/login")
    data object Register : Route("/register")
    data object ForgotPassword : Route("/forgot-password")

    // Authenticated routes
    data object Home : Route("/")
    data object Contests : Route("/contests")
    data class ContestDetail(val contestId: String) : Route("/contests/$contestId")
    data class ContestLeaderboard(val contestId: String) : Route("/contests/$contestId/leaderboard")
    data class ContestEntryView(val contestId: String, val entryId: String) : Route("/contests/$contestId/entry/$entryId")
    data class GameDay(val contestId: String, val entryId: String) : Route("/contests/$contestId/live/$entryId")
    data class Lineup(
        val contestId: String,
        val entryId: String? = null,
        val lineupId: String? = null
    ) : Route(
        if (entryId != null) "/contests/$contestId/lineup/$entryId"
        else "/contests/$contestId/lineup"
    )
    data object Matches : Route("/matches")
    data class MatchDetail(val matchId: String) : Route("/matches/$matchId")
    data object Leaderboards : Route("/leaderboards")
    data object Profile : Route("/profile")
    data object Wallet : Route("/wallet")
    data object Settings : Route("/settings")
    data object MyEntries : Route("/my-entries")
    data object Admin : Route("/admin")

    // Bracket Challenge routes
    data object BracketChallenges : Route("/brackets")
    data class BracketChallengeDetail(val challengeId: String) : Route("/brackets/$challengeId")
    data class BracketBuilder(val challengeId: String, val entryId: String) : Route("/brackets/$challengeId/entry/$entryId")
    data class BracketLeaderboard(val challengeId: String) : Route("/brackets/$challengeId/leaderboard")

    // Survivor Contest routes
    data object SurvivorContests : Route("/survivor")
    data class SurvivorContestDetail(val contestId: String) : Route("/survivor/$contestId")
    data class SurvivorEntry(val contestId: String, val entryId: String) : Route("/survivor/$contestId/entry/$entryId")
    data class SurvivorPickSelection(val contestId: String, val entryId: String) : Route("/survivor/$contestId/entry/$entryId/pick")
    data class SurvivorLeaderboard(val contestId: String) : Route("/survivor/$contestId/leaderboard")

    // Skills routes
    data object Skills : Route("/skills")
    data class SkillLeaderboard(val category: String) : Route("/skills/$category")
    data class PlayerSkillDetail(val playerId: String) : Route("/skills/player/$playerId")

    companion object {
        /**
         * Parse a URL path into a Route object.
         * Returns Home if path cannot be parsed.
         */
        fun fromPath(path: String): Route {
            val cleanPath = path.trimEnd('/')

            return when {
                cleanPath == "" || cleanPath == "/" -> Home
                cleanPath == "/login" -> Login
                cleanPath == "/register" -> Register
                cleanPath == "/forgot-password" -> ForgotPassword
                cleanPath == "/contests" -> Contests
                cleanPath == "/matches" || cleanPath == "/live" -> Matches
                cleanPath == "/leaderboards" -> Leaderboards
                cleanPath == "/profile" -> Profile
                cleanPath == "/wallet" -> Wallet
                cleanPath == "/settings" -> Settings
                cleanPath == "/my-entries" -> MyEntries
                cleanPath == "/admin" -> Admin
                cleanPath == "/brackets" -> BracketChallenges
                cleanPath == "/survivor" -> SurvivorContests
                cleanPath == "/skills" -> Skills

                // Contest routes with IDs
                cleanPath.matches(Regex("^/contests/([^/]+)/leaderboard$")) -> {
                    val id = cleanPath.removePrefix("/contests/").removeSuffix("/leaderboard")
                    ContestLeaderboard(id)
                }
                cleanPath.matches(Regex("^/contests/([^/]+)/entry/([^/]+)$")) -> {
                    val parts = cleanPath.removePrefix("/contests/").split("/entry/")
                    ContestEntryView(parts[0], parts[1])
                }
                // Game Day live view route
                cleanPath.matches(Regex("^/contests/([^/]+)/live/([^/]+)$")) -> {
                    val parts = cleanPath.removePrefix("/contests/").split("/live/")
                    GameDay(parts[0], parts[1])
                }
                // Edit lineup route (must be before generic lineup route)
                cleanPath.matches(Regex("^/contests/([^/]+)/lineup/([^/]+)$")) -> {
                    val parts = cleanPath.removePrefix("/contests/").split("/lineup/")
                    Lineup(contestId = parts[0], entryId = parts[1], lineupId = null)
                }
                // New lineup route
                cleanPath.matches(Regex("^/contests/([^/]+)/lineup$")) -> {
                    val id = cleanPath.removePrefix("/contests/").removeSuffix("/lineup")
                    Lineup(id)
                }
                cleanPath.matches(Regex("^/contests/([^/]+)$")) -> {
                    val id = cleanPath.removePrefix("/contests/")
                    ContestDetail(id)
                }

                // Match detail routes (supports both /matches/:id and /live/:id for backwards compatibility)
                cleanPath.matches(Regex("^/matches/([^/]+)$")) -> {
                    val id = cleanPath.removePrefix("/matches/")
                    MatchDetail(id)
                }
                cleanPath.matches(Regex("^/live/([^/]+)$")) -> {
                    val id = cleanPath.removePrefix("/live/")
                    MatchDetail(id)
                }

                // Bracket routes
                cleanPath.matches(Regex("^/brackets/([^/]+)/entry/([^/]+)$")) -> {
                    val parts = cleanPath.removePrefix("/brackets/").split("/entry/")
                    BracketBuilder(parts[0], parts[1])
                }
                cleanPath.matches(Regex("^/brackets/([^/]+)/leaderboard$")) -> {
                    val id = cleanPath.removePrefix("/brackets/").removeSuffix("/leaderboard")
                    BracketLeaderboard(id)
                }
                cleanPath.matches(Regex("^/brackets/([^/]+)$")) -> {
                    val id = cleanPath.removePrefix("/brackets/")
                    BracketChallengeDetail(id)
                }

                // Survivor routes
                cleanPath.matches(Regex("^/survivor/([^/]+)/entry/([^/]+)/pick$")) -> {
                    val parts = cleanPath.removePrefix("/survivor/").removeSuffix("/pick").split("/entry/")
                    SurvivorPickSelection(parts[0], parts[1])
                }
                cleanPath.matches(Regex("^/survivor/([^/]+)/entry/([^/]+)$")) -> {
                    val parts = cleanPath.removePrefix("/survivor/").split("/entry/")
                    SurvivorEntry(parts[0], parts[1])
                }
                cleanPath.matches(Regex("^/survivor/([^/]+)/leaderboard$")) -> {
                    val id = cleanPath.removePrefix("/survivor/").removeSuffix("/leaderboard")
                    SurvivorLeaderboard(id)
                }
                cleanPath.matches(Regex("^/survivor/([^/]+)$")) -> {
                    val id = cleanPath.removePrefix("/survivor/")
                    SurvivorContestDetail(id)
                }

                // Skills routes
                cleanPath.matches(Regex("^/skills/player/([^/]+)$")) -> {
                    val id = cleanPath.removePrefix("/skills/player/")
                    PlayerSkillDetail(id)
                }
                cleanPath.matches(Regex("^/skills/(serve|forehand|backhand|volleys|fighting)$")) -> {
                    val category = cleanPath.removePrefix("/skills/")
                    SkillLeaderboard(category)
                }

                else -> Home
            }
        }
    }
}

/**
 * Navigation state holder with browser history integration.
 * Supports back/forward buttons, bookmarking, and URL sharing.
 */
object NavigationState {
    private var currentRoute: Route = Route.Home
    private val listeners = mutableListOf<(Route) -> Unit>()
    private var isInitialized = false

    fun getCurrentRoute(): Route = currentRoute

    /**
     * Initialize the navigation system.
     * Sets up browser history listeners and parses the initial URL.
     * Should be called once on app startup.
     */
    fun initialize() {
        if (isInitialized) return
        isInitialized = true

        // Listen for browser back/forward button clicks
        window.onpopstate = { event ->
            val path = window.location.pathname
            val route = Route.fromPath(path)
            currentRoute = route
            listeners.forEach { it(route) }
            Unit
        }

        // Parse initial URL on page load
        val initialPath = window.location.pathname
        currentRoute = Route.fromPath(initialPath)
    }

    /**
     * Navigate to a route, updating browser history.
     * @param route The route to navigate to
     * @param pushState If true (default), adds entry to browser history.
     *                  If false, replaces current history entry.
     */
    fun navigate(route: Route, pushState: Boolean = true) {
        if (currentRoute == route) return

        currentRoute = route

        // Update browser URL
        if (pushState) {
            window.history.pushState(null, "", route.path)
        } else {
            window.history.replaceState(null, "", route.path)
        }

        listeners.forEach { it(route) }
    }

    /**
     * Navigate back in browser history.
     */
    fun goBack() {
        window.history.back()
    }

    /**
     * Navigate forward in browser history.
     */
    fun goForward() {
        window.history.forward()
    }

    fun addListener(listener: (Route) -> Unit) {
        listeners.add(listener)
    }

    fun removeListener(listener: (Route) -> Unit) {
        listeners.remove(listener)
    }
}
